import { useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { toast } from "sonner";
import { Flag, Pencil, Check, X } from "lucide-react";

const ORDER = ["csalpha", "alpha2", "alpha4", "wetester"];

export const GoalCard = ({ goal, onSaved, ops = false }) => {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(goal.target);
  const pct = Math.min(100, goal.pct);
  const growth = goal.month_start_active != null ? goal.active - goal.month_start_active : null;

  const save = async () => {
    const n = parseInt(val, 10);
    if (!n || n < 1) return toast.error("Informe uma meta válida");
    await api.put("/settings/goal", { goal_clients: n });
    toast.success("Meta atualizada");
    setEdit(false);
    onSaved();
  };

  return (
    <div data-testid="goal-card" className="relative overflow-hidden rounded-2xl border border-[#1F293D] bg-[#111827] p-6 fade-up">
      <div className="pointer-events-none absolute -left-20 -top-24 h-56 w-56 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-200">
            <Flag className="h-4 w-4 text-amber-400" /> Meta do mês
          </h3>
          <div className="mt-3 flex items-baseline gap-2">
            <span data-testid="goal-active" className="font-mono text-3xl font-extrabold text-white sm:text-4xl">{goal.active.toLocaleString("pt-BR")}</span>
            <span className="text-slate-500">/</span>
            {ops ? <span data-testid="goal-target" className="font-mono text-xl font-bold text-amber-300">{goal.target.toLocaleString("pt-BR")}</span> : edit ? (
              <span className="flex items-center gap-1">
                <input data-testid="goal-input" type="number" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()}
                  className="w-28 rounded-lg border border-[#202D45] bg-[#0F141F] px-2 py-1 font-mono text-lg text-slate-100 focus:border-amber-400 focus:outline-none" autoFocus />
                <button data-testid="goal-save" onClick={save} className="rounded-lg bg-emerald-500/15 p-1.5 text-emerald-300 hover:bg-emerald-500/25"><Check className="h-4 w-4" /></button>
                <button data-testid="goal-cancel" onClick={() => { setEdit(false); setVal(goal.target); }} className="rounded-lg bg-slate-700/30 p-1.5 text-slate-400 hover:text-slate-200"><X className="h-4 w-4" /></button>
              </span>
            ) : (
              <button data-testid="goal-edit" onClick={() => setEdit(true)} className="group flex items-center gap-1.5 font-mono text-xl font-bold text-amber-300 hover:text-amber-200">
                <span data-testid="goal-target">{goal.target.toLocaleString("pt-BR")}</span>
                <Pencil className="h-3.5 w-3.5 opacity-50 group-hover:opacity-100" />
              </button>
            )}
            <span className="text-sm text-slate-500">clientes ativos</span>
          </div>
          {goal.by_account?.length > 1 && (
            <div data-testid="goal-by-account" className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {[...goal.by_account].sort((x, y) => ORDER.indexOf(x.account) - ORDER.indexOf(y.account)).map((a) => (
                <span key={a.account} data-testid={`goal-acc-${a.account}`} className="text-xs text-slate-400">
                  <span className="text-slate-500">{a.account}</span> <span className="font-mono font-semibold text-slate-200">{a.active.toLocaleString("pt-BR")}</span>
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-slate-500">Faltam</div>
          <div data-testid="goal-missing" className="font-mono text-3xl font-extrabold text-amber-300">{goal.missing.toLocaleString("pt-BR")}</div>
          <div className="text-xs text-slate-500">clientes</div>
        </div>
      </div>

      <div className="relative mt-5">
        <div className="mb-1.5 flex justify-between text-xs">
          <span className="text-slate-400">{goal.missing ? "Progresso" : "Meta batida!"}</span>
          <span data-testid="goal-pct" className="font-mono font-semibold text-slate-200">{goal.pct}%</span>
        </div>
        <div className="h-3 w-full overflow-hidden rounded-full border border-[#1F293D] bg-[#0F141F]">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-500 via-amber-400 to-emerald-400 transition-[width] duration-700 ease-out" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className={`relative mt-5 grid grid-cols-2 gap-3 ${ops ? "" : "sm:grid-cols-4"}`}>
        {[
          ["goal-per-day", "Ritmo para bater a meta", `+${goal.per_day.toLocaleString("pt-BR")} clientes/dia`, `faltam ${goal.missing.toLocaleString("pt-BR")} em ${goal.days_left} dias`],
          ...(ops ? [] : [["goal-mrr-missing", "Receita que falta", fmtBRL(goal.mrr_missing), "por mês, no ticket atual"],
            ["goal-mrr-at-goal", "Receita na meta", fmtBRL(goal.mrr_at_goal), `ticket ${fmtBRL(goal.ticket)}`]]),
          ["goal-growth", "Crescimento no mês", growth == null ? "-" : `${growth >= 0 ? "+" : ""}${growth.toLocaleString("pt-BR")}`,
            goal.month_start_date ? `desde ${new Date(`${goal.month_start_date}T12:00:00`).toLocaleDateString("pt-BR")}` : ""],
        ].map(([tid, label, value, sub]) => (
          <div key={tid} data-testid={tid} className="rounded-xl border border-[#1F293D] bg-[#0F141F]/80 px-3 py-2.5">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="mt-0.5 font-mono text-base font-bold text-slate-100">{value}</div>
            <div className="text-[11px] text-slate-500">{sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
